import { isEqual, pick, pull } from '@sama/micro-dash';
import { keys } from 'lodash';
import { BehaviorSubject } from 'rxjs';
import { isSensorFusionOutput } from '../../../../../../cross-team-shared/misc-utils/output-utils';
import { AbstractUndoManager } from '../../../../../../cross-team-shared/state/abstract-undo-manager';
import { ShapeGroupOrderState } from '../../../../../../team-apis/content/content-connector';
import { UndoRedoService } from '../../../../../../team-apis/content/undo-redo-service';
import { AbstractOutput } from '../../../../../../team-apis/otterdam/outputs/output';
import { getOutputById } from '../../../../../h3ml/dsl/sensor-fusion/sensor-fusion-output.utils';
import { FusionState } from '../../../../../h3ml/state/fusion-state';
import { TaskWorkspaceState } from '../../../../../h3ml/state/task-workspace-state';
import { getShapeInfoFromOutputAnnotationInfo } from '../../../../../h3ml/state/task-workspace-state.utils';
import { TaskWorkspaceStore } from '../../../../../h3ml/state/task-workspace-store';

type UndoState = Pick<FusionState, 'isRightPanelExpanded'> & {
  selectedOutputName: string;
  shapeInfo: StrictRecord<AbstractOutput['name'], ShapeGroupOrderState>;
};

/**
 * UndoService is the fusion level service that handles undoing and redoing the
 * FusionState. It has the extra responsibility of propagating push/undo/redo
 * actions to child services.
 *
 * Changes should be pushed to the undo stack once (and only once) per separate
 * user action (and only for actions that affect the answer, not transient
 * changes).
 */
export class UndoService extends AbstractUndoManager<
  TaskWorkspaceState,
  UndoState
> {
  override canUndo$ = new BehaviorSubject(false);
  override canRedo$ = new BehaviorSubject(false);

  private childUndoServices: UndoRedoService[] = [];
  private fusionId: number;
  private fusedOutputNames: string[];

  constructor(store: TaskWorkspaceStore, output: AbstractOutput) {
    super(store);
    if (isSensorFusionOutput(output)) {
      this.fusionId = output.id;
      this.fusedOutputNames = output.meta.fused_outputs.map(
        (fusedOutput) =>
          getOutputById(store.state().project, fusedOutput.output_id).name,
      );
    } else {
      this.fusionId = output.id;
      this.fusedOutputNames = [output.name];
    }
  }

  addChild(undoService: UndoRedoService) {
    this.childUndoServices.push(undoService);
  }

  removeChild(undoService: UndoRedoService) {
    pull(this.childUndoServices, undoService);
  }

  override canRedo() {
    return (
      super.canRedo() &&
      this.getChildUndoServices().every((service) => service.canRedo())
    );
  }

  override canUndo() {
    return (
      super.canUndo() &&
      this.getChildUndoServices().every((service) => service.canUndo())
    );
  }

  override pushCurrentState(
    options: {
      collectKey?: string | undefined;
      collectDebounce?: number | undefined;
    } = {},
  ) {
    const pushResults = this.getChildUndoServices().map((child) =>
      child.pushCurrentState(options),
    );
    pushResults.push(super.pushCurrentState(options));
    this.emitChanges();
    return {
      hasTrackedChanges: pushResults.some((result) => result.hasTrackedChanges),
    };
  }

  override undo() {
    // need to undo this first or else canUndo will return false because the
    // child can't undo anymore. more importantly, we need to undo the selection
    // first so that the annotation state undo service can calculate correctly
    // tagEditorHidden
    super.undo();
    this.childUndoServices.forEach((child) => child.undo());

    // need to recalculate canUndo$/canRedo$ because the children may have
    // changed their state
    this.emitChanges();
  }

  override redo() {
    // need to redo this first or else canRedo will return false because the
    // child can't redo anymore. more importantly, we need to undo the selection
    // first so that the annotation state undo service can calculate correctly
    // tagEditorHidden
    super.redo();
    this.childUndoServices.forEach((child) => child.redo());

    // need to recalculate canUndo$/canRedo$ because the children may have
    // changed their state
    this.emitChanges();
  }

  override reset() {
    super.reset();
    this.getChildUndoServices().forEach((child) => child.reset());
  }

  protected override applyUndoState(
    stateToApply: UndoState,
    undoOrRedo: 'undo' | 'redo',
    stateToOverwrite: UndoState,
  ) {
    const newerState = undoOrRedo === 'undo' ? stateToOverwrite : stateToApply;

    this.store.getRootStore().batch(() => {
      this.store('selectedOutputName').set(newerState.selectedOutputName);
      this.store('fusions')(this.fusionId).assign({
        isRightPanelExpanded: newerState.isRightPanelExpanded,
      });
      keys(stateToApply.shapeInfo).forEach((outputName) => {
        this.store('outputAnnotationInfo')(outputName).assign(
          stateToApply.shapeInfo[outputName],
        );
      });
    });
  }

  protected override extractUndoState(state: TaskWorkspaceState): UndoState {
    return {
      selectedOutputName: state.selectedOutputName,
      // fix me if you change this code

      // @ts-ignore -- fix me
      shapeSelection: state.fusions[this.fusionId]?.shapeSelection,
      // @ts-ignore -- fix me
      shapeInfo: getShapeInfoFromOutputAnnotationInfo(
        state.outputAnnotationInfo,
      ),
      // fix me if you change this code

      // @ts-ignore -- fix me
      isRightPanelExpanded: state.fusions[this.fusionId]?.isRightPanelExpanded,
    };
  }

  protected override hasTrackedChanges(
    before: UndoState | undefined,
    after: UndoState,
  ) {
    return !isEqual(
      // fix me if you change this code
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- fix me!
      pick(before?.shapeInfo, ...(this.fusedOutputNames ?? [])),
      // fix me if you change this code
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- fix me!
      pick(after.shapeInfo, ...(this.fusedOutputNames ?? [])),
    );
  }

  private getChildUndoServices() {
    // unfortunately, the base constructor calls canUndo, canRedo and
    // pushCurrentState so the array is not initialized yet
    // fix me if you change this code
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- fix me!
    return this.childUndoServices ?? [];
  }

  private emitChanges() {
    // fix me if you change this code
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- fix me!
    this.canUndo$?.next(this.canUndo());
    // fix me if you change this code
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- fix me!
    this.canRedo$?.next(this.canRedo());
  }
}
